import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Typography,
  Box,
  Divider
} from '@mui/material';

interface TestPushDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TestPushData) => void;
  isSubmitting: boolean;
  templates: any[];
  isLoadingTemplates: boolean;
  channelType: string;
}

export interface TestPushData {
  fcm_token: string;
  template_id: string;
  use_custom_template: boolean;
  test_title: string;
  test_body: string;
}

const TestPushDialog: React.FC<TestPushDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  templates,
  isLoadingTemplates,
  channelType
}) => {
  const [testData, setTestData] = useState<TestPushData>({
    fcm_token: '',
    template_id: '',
    use_custom_template: false,
    test_title: '',
    test_body: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  useEffect(() => {
    // Reset data when dialog opens
    if (open) {
      setTestData({
        fcm_token: '',
        template_id: '',
        use_custom_template: false,
        test_title: '',
        test_body: ''
      });
      setErrors({});
    }
  }, [open]);
  
  const handleChange = (field: keyof TestPushData, value: string | boolean) => {
    setTestData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (channelType === 'push') {
      if (!testData.fcm_token && !testData.template_id && !testData.use_custom_template) {
        newErrors.form = 'Please provide either FCM token, select a template, or enter custom message details';
      }
      
      if (testData.use_custom_template) {
        if (!testData.test_title) {
          newErrors.test_title = 'Test title is required';
        }
        if (!testData.test_body) {
          newErrors.test_body = 'Test message body is required';
        }
      } else if (!testData.template_id) {
        newErrors.template_id = 'Please select a template';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(testData);
    }
  };
  
  const renderPushTestFields = () => (
    <>
      <Typography variant="subtitle2" gutterBottom>
        For testing push notifications, you can either:
      </Typography>
      
      <RadioGroup
        value={testData.use_custom_template ? 'custom' : 'template'}
        onChange={(e) => handleChange('use_custom_template', e.target.value === 'custom')}
      >
        <FormControlLabel value="template" control={<Radio />} label="Select an existing template" />
        <FormControlLabel value="custom" control={<Radio />} label="Enter custom message" />
      </RadioGroup>
      
      {testData.use_custom_template ? (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            margin="dense"
            label="Test Notification Title"
            value={testData.test_title}
            onChange={(e) => handleChange('test_title', e.target.value)}
            error={!!errors.test_title}
            helperText={errors.test_title || ''}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Test Notification Body"
            multiline
            rows={3}
            value={testData.test_body}
            onChange={(e) => handleChange('test_body', e.target.value)}
            error={!!errors.test_body}
            helperText={errors.test_body || ''}
          />
        </Box>
      ) : (
        <FormControl fullWidth margin="dense" error={!!errors.template_id}>
          <InputLabel>Select Template</InputLabel>
          <Select
            value={testData.template_id}
            label="Select Template"
            onChange={(e) => handleChange('template_id', e.target.value)}
            disabled={isLoadingTemplates}
          >
            {isLoadingTemplates ? (
              <MenuItem disabled>Loading templates...</MenuItem>
            ) : templates.length > 0 ? (
              templates.map(template => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No templates available</MenuItem>
            )}
          </Select>
          {errors.template_id && <Typography color="error" variant="caption">{errors.template_id}</Typography>}
        </FormControl>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      <TextField
        fullWidth
        margin="dense"
        label="FCM Token (optional)"
        placeholder="Enter a specific FCM token to test with"
        value={testData.fcm_token}
        onChange={(e) => handleChange('fcm_token', e.target.value)}
        helperText="If provided, notification will only be sent to this device. Otherwise, it will be sent to all active devices of test users."
      />
      
      {errors.form && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {errors.form}
        </Typography>
      )}
    </>
  );
  
  const renderEmailTestFields = () => (
    <Typography>
      Email test functionality will validate the connection to the email provider and send a test email.
    </Typography>
  );
  
  const renderSmsTestFields = () => (
    <Typography>
      SMS test functionality will validate the connection to the SMS provider and send a test message.
    </Typography>
  );
  
  const renderWhatsAppTestFields = () => (
    <Typography>
      WhatsApp test functionality will validate the connection to the WhatsApp provider and send a test message.
    </Typography>
  );
  
  const renderTestFields = () => {
    switch (channelType) {
      case 'push':
        return renderPushTestFields();
      case 'email':
        return renderEmailTestFields();
      case 'sms':
        return renderSmsTestFields();
      case 'whatsapp':
        return renderWhatsAppTestFields();
      default:
        return (
          <Typography>
            Testing is not available for this channel type.
          </Typography>
        );
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{`Test ${channelType === 'push' ? 'Push Notification' : channelType === 'email' ? 'Email' : channelType === 'sms' ? 'SMS' : 'WhatsApp'} Channel`}</DialogTitle>
      <DialogContent>
        {renderTestFields()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained" 
          disabled={isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={20} />}
        >
          {isSubmitting ? 'Testing...' : 'Test Channel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestPushDialog;
